import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AdminNav from "@/components/AdminNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

export default function AdminSettings() {
    const [s, setS] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/admin/settings").then((r) => setS(r.data)).catch((e) => toast.error(formatApiError(e)));
    }, []);

    if (!s) return (<div className="min-h-screen bg-white"><AdminNav /><div className="p-8 text-center text-gray-500">Caricamento…</div></div>);

    const toggleDay = (idx) => {
        const set = new Set(s.visible_days);
        if (set.has(idx)) set.delete(idx); else set.add(idx);
        setS({ ...s, visible_days: [...set].sort() });
    };

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/admin/settings", {
                display_name: s.display_name,
                admin_email: s.admin_email,
                timezone: s.timezone,
                default_start_hour: Number(s.default_start_hour),
                default_end_hour: Number(s.default_end_hour),
                visible_days: s.visible_days,
                email_footer: s.email_footer,
            });
            setS(data);
            toast.success("Impostazioni salvate");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <div className="min-h-screen bg-white">
            <AdminNav />
            <div className="max-w-2xl mx-auto px-4 py-6">
                <h2 className="text-xl font-bold mb-4">Impostazioni</h2>
                <div className="border border-gray-200 p-5 space-y-4">
                    <div>
                        <Label>Nome visualizzato</Label>
                        <Input value={s.display_name} onChange={(e) => setS({ ...s, display_name: e.target.value })} data-testid="settings-display-name" />
                    </div>
                    <div>
                        <Label>Email amministratore (notifiche)</Label>
                        <Input type="email" value={s.admin_email} onChange={(e) => setS({ ...s, admin_email: e.target.value })} data-testid="settings-admin-email" />
                    </div>
                    <div>
                        <Label>Fuso orario</Label>
                        <Input value={s.timezone} onChange={(e) => setS({ ...s, timezone: e.target.value })} data-testid="settings-timezone" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Ora iniziale (0-23)</Label>
                            <Input type="number" min={0} max={23} value={s.default_start_hour} onChange={(e) => setS({ ...s, default_start_hour: e.target.value })} data-testid="settings-start-hour" />
                        </div>
                        <div>
                            <Label>Ora finale (1-23)</Label>
                            <Input type="number" min={1} max={23} value={s.default_end_hour} onChange={(e) => setS({ ...s, default_end_hour: e.target.value })} data-testid="settings-end-hour" />
                        </div>
                    </div>
                    <div>
                        <Label>Giorni visibili</Label>
                        <div className="flex gap-2 mt-2 flex-wrap">
                            {DAYS.map((d, i) => (
                                <button
                                    key={d}
                                    type="button"
                                    onClick={() => toggleDay(i)}
                                    className={`px-3 py-1.5 text-sm border ${s.visible_days.includes(i) ? "border-green-700 bg-green-50 text-green-800" : "border-gray-200 text-gray-500"}`}
                                    data-testid={`day-toggle-${i}`}
                                >{d}</button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <Label>Testo email (firma)</Label>
                        <Textarea rows={3} value={s.email_footer || ""} onChange={(e) => setS({ ...s, email_footer: e.target.value })} data-testid="settings-footer" />
                    </div>
                    <Button onClick={save} disabled={saving} className="bg-green-700 hover:bg-green-800 text-white" data-testid="save-settings-btn">
                        {saving ? "Salvo…" : "SALVA"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
